import { create } from 'zustand';

import type { AnalyticsAdapter } from '@/adapters/analytics/types';
import type { UserPreferencesRepository } from '@/adapters/database/types';
import type { Logger } from '@/adapters/logger/types';
import { ANALYTICS_EVENTS, ANALYTICS_PARAMS } from '@/constants/analytics';

export const OnboardingStep = {
  welcome: 0,
  record: 1,
  save: 2,
  notify: 3,
} as const;

export type OnboardingStepValue = (typeof OnboardingStep)[keyof typeof OnboardingStep];

const STEP_VALUES = Object.values(OnboardingStep);
const MAX_STEP = Math.max(...STEP_VALUES);

export interface OnboardingAdapters {
  userPreferences: UserPreferencesRepository;
  logger: Logger;
  analytics: AnalyticsAdapter;
}

export interface OnboardingState {
  isCompleted: boolean;
  isSkipped: boolean;
  isLoading: boolean;
  currentStep: OnboardingStepValue;
  userId: string | null;
}

export interface OnboardingActions {
  loadOnboarding: (userId: string) => Promise<void>;
  completeOnboarding: (userId: string) => Promise<void>;
  skipOnboarding: (userId: string, step: OnboardingStepValue) => Promise<void>;
  startOnboarding: () => void;
  nextStep: () => void;
  goToStep: (step: OnboardingStepValue) => void;
}

const getOnboardingCompletedStorageKey = (userId: string) => `onboarding.completed:${userId}`;
const getOnboardingSkippedStorageKey = (userId: string) => `onboarding.skipped:${userId}`;

const clampStep = (step: number): OnboardingStepValue =>
  Math.max(0, Math.min(MAX_STEP, step)) as OnboardingStepValue;

const createOnboardingStore = (adapters: OnboardingAdapters) =>
  create<OnboardingState & OnboardingActions>((set, get) => ({
    isCompleted: false,
    isSkipped: false,
    isLoading: true,
    currentStep: OnboardingStep.welcome,
    userId: null,

    loadOnboarding: async (userId: string) => {
      try {
        const completedKey = getOnboardingCompletedStorageKey(userId);
        const skippedKey = getOnboardingSkippedStorageKey(userId);
        const [storedCompleted, storedSkipped] = await Promise.all([
          adapters.userPreferences.getValue(userId, completedKey),
          adapters.userPreferences.getValue(userId, skippedKey),
        ]);
        set({
          isCompleted: storedCompleted === 'true',
          isSkipped: storedSkipped === 'true',
          isLoading: false,
          userId,
        });
      } catch (error) {
        adapters.logger.captureException(error);
        set({ isLoading: false, userId });
      }
    },

    completeOnboarding: async (userId: string) => {
      set({ isCompleted: true });
      adapters.analytics.track(ANALYTICS_EVENTS.APP_ONBOARDING_COMPLETED);

      try {
        const key = getOnboardingCompletedStorageKey(userId);
        await adapters.userPreferences.setValue(userId, key, 'true');
      } catch (error) {
        adapters.logger.captureException(error);
      }
    },

    skipOnboarding: async (userId: string, step: OnboardingStepValue) => {
      set({ isSkipped: true });
      adapters.analytics.track(ANALYTICS_EVENTS.APP_ONBOARDING_SKIPPED, {
        [ANALYTICS_PARAMS.STEP]: step,
      });

      try {
        const key = getOnboardingSkippedStorageKey(userId);
        await adapters.userPreferences.setValue(userId, key, 'true');
      } catch (error) {
        adapters.logger.captureException(error);
      }
    },

    startOnboarding: () => {
      adapters.analytics.track(ANALYTICS_EVENTS.APP_ONBOARDING_STARTED);
      set({ currentStep: OnboardingStep.welcome });
    },

    nextStep: () => {
      const nextStep = clampStep(get().currentStep + 1);

      adapters.analytics.track(ANALYTICS_EVENTS.APP_ONBOARDING_STEP_CHANGED, {
        [ANALYTICS_PARAMS.STEP]: nextStep,
      });
      set({ currentStep: nextStep });
    },

    goToStep: (step: OnboardingStepValue) => {
      const currentStep = clampStep(step);

      adapters.analytics.track(ANALYTICS_EVENTS.APP_ONBOARDING_STEP_CHANGED, {
        [ANALYTICS_PARAMS.STEP]: currentStep,
      });
      set({ currentStep });
    },
  }));

export { createOnboardingStore };
